<script setup lang="ts">
import { computed, ref, type Ref, toRefs, onMounted, watch } from 'vue';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import cloneDeep from 'lodash.clonedeep';
import AdvancedSettingsInput from '@/components/robvision/AdvancedSettingsInput.vue';
import { cropDataURLByMask, resampleMaskTo } from '@/helpers/RobvisionHelper';
import {
  convertImageFileToPNG,
  downsampleImageForInference,
  type DownsampledImage,
} from '@/helpers/ImageHelper';
import { convertFileToBase64, convertUrlToFile } from '@/helpers/FileHelper';
import { findConnectedComponents } from '@/helpers/MaskAnalysis';
import {
  MAX_FILE_BYTES,
  MAX_FILE_MB,
  MAX_IMAGES,
} from '@/helpers/RobvisionConstants';
import {
  type NewCADProject,
  EMPTY_NEW_CAD_PROJECT,
  type MaskData,
  type SamMask,
  type VisionProjectType,
  type AdvancedSettings,
  DEFAULT_IMAGE_ADVANCED_SETTINGS,
} from '@/models/RobVision';
import {
  createVisionProject,
  uploadImageAnnotation,
  addAdvancedSettings,
  deleteProject,
  uploadCustomBackgrounds,
} from '@/api/RobVisionApi';
import LabelingTool from '@/components/robvision/LabelingTool.vue';
import ImageProjectOverview from '@/components/robvision/ImageProjectOverview.vue';
import CustomBackgroundsInput from '@/components/robvision/CustomBackgroundsInput.vue';
import gettext from '@/i18n/gettext';

const props = defineProps<{
  isImageOpen: boolean;
  projectName: string;
  projectType: VisionProjectType;
}>();
const { isImageOpen, projectName, projectType } = toRefs(props);
const emit = defineEmits([
  'update:isImageOpen',
  'part-created',
  'file-too-large',
]);

const advancedSettings = ref<AdvancedSettings>(
  cloneDeep(DEFAULT_IMAGE_ADVANCED_SETTINGS),
);
const { $gettext } = gettext;
const showAdvancedSettings = ref(false);
const createError = ref('');
const loadingSpinnerStore = useLoadingSpinnerStore();
const createProjectValid = ref(false);
const newPart = ref<NewCADProject>(cloneDeep(EMPTY_NEW_CAD_PROJECT));
const currentStepIndex: Ref<number> = ref(1);
const imagesToUpload: Ref<File[]> = ref([]);
const cutouts = ref<{ [key: number]: string | null }>({});
const worker = ref<Worker | null>(null);
const workerReady = ref(false);
const imagePreview = ref<string[]>([]);
const modelStatus = ref('Initializing...');
const imageEmbeddingStatus = ref<{ [key: number]: string }>({});
const imageClasses = ref<{ [key: number]: string }>({});
const imageSizes = ref<{
  [key: number]: { width: number; height: number; depth: number };
}>({});

// State management for embeddings and masks (worker keeps embeddings, we track status)
const embeddingsReady = ref<{ [key: number]: boolean }>({});
const samMasks = ref<{ [key: number]: SamMask }>({});
const labeledMasks = ref<{ [key: number]: MaskData }>({});
const croppedCutout = ref<{ [key: number]: string | null }>({});
const imageMeta = ref<{ [key: number]: DownsampledImage }>({});
const autoResizeOnUpload = ref(true);
const uploadFailure = ref<{ message: string } | null>(null);
const isRetrying = ref(false);

// Returns the labeled mask resampled up to the *original* image resolution
// for the given slot, or `null` if no mask has been drawn. When the source
// image was small enough to skip downsampling (`scale === 1`), the stored
// mask already matches the original image dims and is returned as-is.
const getMaskAtOriginalRes = (index: number): MaskData | null => {
  const mask = labeledMasks.value[index];
  const meta = imageMeta.value[index];
  if (!mask) {
    return null;
  }
  if (!meta || meta.scale === 1) {
    return mask;
  }
  return resampleMaskTo(mask, meta.originalWidth, meta.originalHeight);
};

const stagingFiles = ref<File[]>([]);
const backgroundFiles = ref<File[]>([]);
const backgroundsValid = ref(true);
const labelingToolValidity = ref<boolean[]>([]);
const hasMaskPixelsByIndex = ref<boolean[]>([]);
const fileIds = ref<string[]>([]);
const resizedOnUpload = ref<boolean[]>([]);

// Plain `let` (rather than `ref`) for non-reactive counters and tokens. In
// Vue 3 `<script setup>` the script body IS the setup function, so each
// component instance gets its own closure — these are per-instance, not
// module-scoped, despite their top-of-file appearance.
let nextFileId = 0;

// Per-slot epoch token used to discriminate stale worker messages. We bump
// the slot's epoch whenever its image content changes (file added, removed,
// or shifted by a removal, or session reset). The worker echoes the epoch
// back in its replies and we drop anything tagged with a stale value, so
// in-flight embeddings or masks from a previous image (or a previous
// project) cannot land in the wrong slot.
const slotEpoch = ref<Record<number, number>>({});
let nextEpoch = 1;
const bumpSlotEpoch = (index: number) => {
  nextEpoch += 1;
  slotEpoch.value[index] = nextEpoch;
  return nextEpoch;
};
const epochOf = (index: number) => slotEpoch.value[index] ?? 0;

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const fileValidationErrors = computed<string[]>(() => {
  const errors: string[] = [];
  if (imagesToUpload.value.length > MAX_IMAGES) {
    errors.push(
      $gettext('Maximum %{n} images allowed.', { n: String(MAX_IMAGES) }),
    );
  }
  if (imagesToUpload.value.some((f) => f.size > MAX_FILE_BYTES)) {
    errors.push(
      $gettext('Some files exceed %{n} MB.', { n: String(MAX_FILE_MB) }),
    );
  }
  return errors;
});

const filesAreValid = computed(
  () =>
    imagesToUpload.value.length > 0 && fileValidationErrors.value.length === 0,
);

// Initialize worker when dialog opens
const initializeWorker = () => {
  if (!worker.value) {
    try {
      // Adjust the path to your worker file
      worker.value = new Worker(
        new URL('@/workers/sam-worker.ts', import.meta.url),
        { type: 'module' },
      );
      worker.value.onmessage = (e: MessageEvent) => {
        const { type, data } = e.data;
        if (type === 'ready') {
          workerReady.value = true;
          modelStatus.value = 'Model loaded successfully!';
          return;
        }
        if (type === 'embeddings_ready') {
          const { imageIndex, epoch } = data;
          if (epoch !== epochOf(imageIndex)) {
            return;
          }
          embeddingsReady.value[imageIndex] = true;
          imageEmbeddingStatus.value[imageIndex] = 'Embeddings ready!';
          return;
        }
        if (type === 'mask_ready') {
          const { mask, scores, imageIndex, epoch } = data;
          if (epoch !== epochOf(imageIndex)) {
            return;
          }
          const plainMask = {
            data: mask.data,
            width: mask.width,
            height: mask.height,
          };
          samMasks.value[imageIndex] = {
            mask: plainMask,
            scores: Array.from(scores),
          };
          return;
        }
        if (type === 'error') {
          const { message, imageIndex } = data;
          console.error('Worker error:', message);
          if (imageIndex !== undefined) {
            imageEmbeddingStatus.value[imageIndex] = `Error: ${message}`;
          } else {
            modelStatus.value = `Error: ${message}`;
          }
        }
      };
      worker.value.postMessage({ type: 'init' });
    } catch (error) {
      console.error('Error initializing worker:', error);
    }
  }
};

async function generateCroppedCutout(id: number) {
  const url = cutouts.value[id];
  const mask = getMaskAtOriginalRes(id);
  if (!url || !mask) {
    return;
  }

  croppedCutout.value[id] = await cropDataURLByMask(url, mask);
}

function grayscaleToRGBAImageData(
  data: Uint8Array,
  width: number,
  height: number,
): ImageData {
  const rgba = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const val = data[i]; // grayscale value
    const offset = i * 4;
    rgba[offset] = val; // R
    rgba[offset + 1] = val; // G
    rgba[offset + 2] = val; // B
    rgba[offset + 3] = 255; // A
  }
  return new ImageData(rgba, width, height);
}

// Read a File's intrinsic pixel dims by decoding it through an offscreen
// <img>. Cheaper than re-encoding through canvas; we only need the natural
// width/height to assert that the mask we're about to upload aligns with
// the image we're about to upload alongside it.
const readImageNaturalDims = (
  file: File,
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

const generateMaskFile = async (maskData: MaskData): Promise<File> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }
  const { width, height, data } = maskData;

  canvas.width = width;
  canvas.height = height;

  const imageData = grayscaleToRGBAImageData(data, width, height);

  ctx.putImageData(imageData, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        throw new Error('Failed to create blob from canvas');
      }
      const file = new File([blob], 'segmentation-mask.png', {
        type: 'image/png',
      });
      resolve(file);
    }, 'image/png');
  });
};

// Aborted at the JS level by resetAndClose / cancel; null when no upload is
// running. We expose it as a module-scoped let so the cancel paths can
// reach in without prop-drilling a controller through every caller.
let uploadAbortController: AbortController | null = null;

class UploadAbortedError extends Error {
  constructor() {
    super('Upload aborted by user');
    this.name = 'UploadAbortedError';
  }
}

const isAbortError = (err: unknown): boolean => {
  if (err instanceof UploadAbortedError) {
    return true;
  }
  if (typeof err === 'object' && err !== null) {
    const { name } = err as { name?: string };
    const { code } = err as { code?: string };
    return (
      name === 'CanceledError' ||
      name === 'AbortError' ||
      code === 'ERR_CANCELED'
    );
  }
  return false;
};

async function withRetry<T>(
  fn: () => Promise<T>,
  retries = 3,
  signal?: AbortSignal,
): Promise<T> {
  for (let attempt = 1; attempt <= retries; attempt++) {
    if (signal?.aborted) {
      throw new UploadAbortedError();
    }
    try {
      return await fn();
    } catch (err) {
      if (isAbortError(err)) {
        throw new UploadAbortedError();
      }
      if (attempt === retries) {
        throw err;
      }
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
    }
  }
  throw new Error('Unreachable');
}

const uploadAllCutouts = async (signal: AbortSignal) => {
  const uuid = await createVisionProject(
    projectName.value,
    projectType.value,
    signal,
  );
  await addAdvancedSettings(uuid, advancedSettings.value, signal);

  // First, build an array of upload‐jobs
  const jobs = imagesToUpload.value
    .map((originalFile, index) => {
      const cutoutDataUrl = cutouts.value[index];
      const mask = labeledMasks.value[index];
      const className = imageClasses.value[index];
      const size = imageSizes.value[index];

      // Skip invalid entries
      if (!cutoutDataUrl || !originalFile || !className || !size) {
        console.warn(`Skipping image at index ${index} due to missing data`);
        return null;
      }

      // Return a promise that does the actual work
      return (async () => {
        if (signal.aborted) {
          throw new UploadAbortedError();
        }
        const cc = findConnectedComponents(mask.data, mask.width, mask.height);
        if (cc.numComponents > 1) {
          throw new Error(
            `Image ${index + 1} has a disconnected mask (${cc.numComponents} parts). ` +
              `Use "Keep largest" or fix the mask before uploading.`,
          );
        }

        const pngFile = await convertImageFileToPNG(originalFile);
        const copiedSize = [
          Number(size.width),
          Number(size.height),
          Number(size.depth),
        ];
        const cutout = await convertUrlToFile(
          cutoutDataUrl,
          `${index}_cutout.png`,
          'image/png',
        );
        const fullResMask = getMaskAtOriginalRes(index) ?? mask;
        // Assert the mask aligns with the image we're about to upload it
        // alongside. The backend pairs them by index and a silent dim
        // mismatch produces a misaligned segmentation, not an obvious
        // failure. Failing loudly here is much easier to triage than the
        // resulting downstream artifact.
        const imageDims = await readImageNaturalDims(pngFile);
        if (
          fullResMask.width !== imageDims.width ||
          fullResMask.height !== imageDims.height
        ) {
          throw new Error(
            `Mask/image dimension mismatch for image ${index + 1}: ` +
              `mask is ${fullResMask.width}x${fullResMask.height} but ` +
              `image is ${imageDims.width}x${imageDims.height}.`,
          );
        }
        const segmentationMask = await generateMaskFile(fullResMask);
        await withRetry(
          () =>
            uploadImageAnnotation({
              visionProjectUuid: uuid,
              label: className,
              size: copiedSize,
              image: pngFile,
              segmentationMask,
              cutout,
              signal,
            }),
          3,
          signal,
        );
      })();
    })
    .filter(Boolean); // drop the nulls

  // Optional deployment-environment backgrounds ride along in the same batch
  if (backgroundFiles.value.length > 0) {
    jobs.push(
      (async () => {
        if (signal.aborted) {
          throw new UploadAbortedError();
        }
        await withRetry(
          () => uploadCustomBackgrounds(uuid, backgroundFiles.value, signal),
          3,
          signal,
        );
      })(),
    );
  }

  // Run all uploads in parallel
  try {
    await Promise.all(jobs);
  } catch (err) {
    // Rollback: delete the partially-created project
    try {
      await deleteProject(uuid);
    } catch {
      /* best-effort cleanup */
    }
    throw err;
  }
  return uuid;
};

const createEmbeddingsForImage = async (imageIndex: number) => {
  if (
    !worker.value ||
    !workerReady.value ||
    !imagesToUpload.value[imageIndex]
  ) {
    return;
  }
  try {
    imageEmbeddingStatus.value[imageIndex] = 'Creating embeddings…';

    let meta = imageMeta.value[imageIndex];
    if (!meta) {
      meta = await downsampleImageForInference(
        imagesToUpload.value[imageIndex],
      );
      imageMeta.value[imageIndex] = meta;
      imagePreview.value[imageIndex] = meta.dataUrl;
    }

    worker.value.postMessage({
      type: 'create_embeddings',
      data: meta.dataUrl,
      imageIndex,
      epoch: epochOf(imageIndex),
    });
  } catch (err) {
    console.error('Error creating embeddings for image:', err);
    imageEmbeddingStatus.value[imageIndex] = 'Error creating embeddings';
  }
};

const createCutoutFromMask = async (index: number): Promise<string | null> => {
  const imageFile = imagesToUpload.value[index];
  const mask = getMaskAtOriginalRes(index);
  if (!mask || !imageFile) {
    console.warn('Cannot create masked image: missing mask or image file');
    return null;
  }

  // Use a data URL rather than URL.createObjectURL: the rest of this dialog
  // is uniformly data-URL based, and previous attempts at object URLs leaked
  // because revoke wasn't paired with creation paths inside async flows.
  const dataUrl = await convertFileToBase64(imageFile);

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d', { alpha: true });
      if (!ctx) {
        console.error('Failed to get 2D context');
        resolve(null);
        return;
      }

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixelData = imageData.data;

      const { data: maskData, width: maskWidth, height: maskHeight } = mask;

      if (maskWidth !== canvas.width || maskHeight !== canvas.height) {
        console.warn(
          `Mask size (${maskWidth}x${maskHeight}) does not match image size (${canvas.width}x${canvas.height})`,
        );
        resolve(null);
        return;
      }

      const numPixels = canvas.width * canvas.height;

      for (let i = 0; i < numPixels; i++) {
        const maskValue = maskData[i];
        if (maskValue === 0) {
          const pixelIndex = i * 4 + 3; // Alpha channel
          pixelData[pixelIndex] = 0; // Fully transparent
        }
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = (err) => {
      console.error('Error loading image for cutout:', err);
      resolve(null);
    };

    img.src = dataUrl;
  });
};

// Generation token for `computeAllCutouts`. Bumped on every new run and on
// `resetAndClose`, so a slow run that resolves after a newer run started
// (e.g. user mashes the stepper Overview button, then closes the dialog)
// is dropped instead of writing stale thumbnails into the now-empty refs.
let cutoutGen = 0;

const computeAllCutouts = async () => {
  cutoutGen += 1;
  const myGen = cutoutGen;

  const cutoutPromises = imagesToUpload.value.map((_, index) =>
    labeledMasks.value[index]
      ? createCutoutFromMask(index)
      : Promise.resolve(null),
  );

  const cutoutResults = await Promise.all(cutoutPromises);
  if (myGen !== cutoutGen) {
    return;
  }

  cutoutResults.forEach((cutout, index) => {
    cutouts.value[index] = cutout;
  });

  await Promise.all(
    imagesToUpload.value.map((_, i) =>
      labeledMasks.value[i] ? generateCroppedCutout(i) : Promise.resolve(null),
    ),
  );
  if (myGen !== cutoutGen) {
    return;
  }
};

const createMaskForImage = (
  imageIndex: number,
  points: Array<{ point: [number, number]; label: number }>,
) => {
  if (!worker.value || !workerReady.value) {
    console.error('Cannot create mask: worker not ready');
    return;
  }

  if (!embeddingsReady.value[imageIndex]) {
    console.error(
      'Cannot create mask: embeddings not ready for image',
      imageIndex,
    );
    return;
  }

  worker.value.postMessage({
    type: 'create_mask',
    data: points,
    imageIndex,
    epoch: epochOf(imageIndex),
  });
};

async function maybeResizeFile(f: File): Promise<File> {
  if (!autoResizeOnUpload.value) {
    return f;
  }
  try {
    const ds = await downsampleImageForInference(f);
    if (ds.scale === 1) {
      return f;
    }
    return await convertUrlToFile(
      ds.dataUrl,
      f.name.replace(/\.\w+$/, '.png'),
      'image/png',
    );
  } catch {
    // If we can't decode for any reason, leave the original file alone —
    // the validation messages will still flag oversized files.
    return f;
  }
}

async function addFiles(newFiles: File[]) {
  if (newFiles.length === 0) {
    return;
  }
  uploadFailure.value = null;

  // Track whether maybeResizeFile actually swapped the file. The display-only
  // downsample for SAM embeddings happens separately below and must NOT be
  // labelled as "resized" — only an actual upload-file replacement counts.
  //
  // After resizing we also normalize to PNG, because the upload pipeline
  // converts to PNG anyway and JPEG → PNG can substantially inflate the
  // file size. Normalizing up front means f.size below reflects the actual
  // upload payload, so the 15 MB validation catches oversized files at
  // the upload step instead of letting them through and failing at Finish.
  const processed = await Promise.all(
    newFiles.map(async (f) => {
      const resized = await maybeResizeFile(f);
      const png =
        resized.type === 'image/png'
          ? resized
          : await convertImageFileToPNG(resized);
      return { file: png, wasResized: resized !== f };
    }),
  );
  const startIndex = imagesToUpload.value.length;

  imagesToUpload.value = [
    ...imagesToUpload.value,
    ...processed.map((p) => p.file),
  ];
  processed.forEach((p, i) => {
    nextFileId += 1;
    fileIds.value.push(`f${nextFileId}`);
    resizedOnUpload.value.push(p.wasResized);
    hasMaskPixelsByIndex.value.push(false);
    bumpSlotEpoch(startIndex + i);
  });

  // Eagerly downsample for display + SAM embeddings. The source File stays
  // intact for full-resolution cutouts and uploads.
  const metas = await Promise.all(
    processed.map((p) => downsampleImageForInference(p.file)),
  );
  metas.forEach((meta, i) => {
    const idx = startIndex + i;
    imageMeta.value[idx] = meta;
    imagePreview.value[idx] = meta.dataUrl;
  });

  if (workerReady.value) {
    metas.forEach((_, i) => createEmbeddingsForImage(startIndex + i));
  }
}

function removeImageAt(index: number) {
  const lastIndex = imagesToUpload.value.length - 1;
  if (index < 0 || index > lastIndex) {
    return;
  }

  imagesToUpload.value.splice(index, 1);
  imagePreview.value.splice(index, 1);
  fileIds.value.splice(index, 1);
  resizedOnUpload.value.splice(index, 1);
  labelingToolValidity.value.splice(index, 1);
  hasMaskPixelsByIndex.value.splice(index, 1);

  const shiftDict = <T,>(dict: Record<number, T>) => {
    delete dict[index];
    for (let i = index + 1; i <= lastIndex; i++) {
      if (i in dict) {
        dict[i - 1] = dict[i];
        delete dict[i];
      } else {
        delete dict[i - 1];
      }
    }
  };
  shiftDict(imageMeta.value);
  shiftDict(embeddingsReady.value);
  shiftDict(samMasks.value);
  shiftDict(imageEmbeddingStatus.value);
  shiftDict(labeledMasks.value);
  shiftDict(imageClasses.value);
  shiftDict(imageSizes.value);
  shiftDict(cutouts.value);
  shiftDict(croppedCutout.value);

  // Bump epochs for every affected slot. Slot `index` is being removed and
  // its old image was at the now-last position — both must invalidate any
  // in-flight worker traffic. After the shift, slots [index..newLast] hold
  // *different* images than the worker's cached embeddings, so they need
  // fresh epochs as well.
  const newLast = imagesToUpload.value.length - 1;
  for (let i = index; i <= lastIndex; i++) {
    bumpSlotEpoch(i);
  }

  // The worker keys its imageState by index too, so positions [index..lastIndex]
  // now point to stale embeddings. Clear those slots and re-issue embeddings
  // for the newly-shifted positions. Positions [0..index-1] are untouched.
  if (worker.value && workerReady.value) {
    for (let i = index; i <= lastIndex; i++) {
      worker.value.postMessage({
        type: 'clear_image',
        imageIndex: i,
        epoch: epochOf(i),
      });
    }
    for (let i = index; i <= newLast; i++) {
      const meta = imageMeta.value[i];
      if (!meta) {
        continue;
      }
      embeddingsReady.value[i] = false;
      imageEmbeddingStatus.value[i] = 'Creating embeddings…';
      worker.value.postMessage({
        type: 'create_embeddings',
        data: meta.dataUrl,
        imageIndex: i,
        epoch: epochOf(i),
      });
    }
  }

  // Step navigation after removal:
  //   - If the user was on the now-removed image's labeling step, jump back
  //     to the upload step so the canvas isn't silently swapped to a
  //     different image's content (which is jarring and risks them editing
  //     the wrong image).
  //   - If they removed the last image while on Overview, step back to
  //     Overview's new position (or to upload if no labeling steps remain).
  //   - Otherwise (they were on an earlier labeling step), keep them where
  //     they are — the shift kept their context intact.
  const removedStepValue = index + 2;
  const newLength = stepItems.value.length;
  if (currentStepIndex.value === removedStepValue) {
    currentStepIndex.value = 1;
  } else if (currentStepIndex.value > newLength) {
    currentStepIndex.value = Math.max(1, newLength - 1);
  } else if (currentStepIndex.value > removedStepValue) {
    // The user was on a step that has shifted down by one.
    currentStepIndex.value -= 1;
  }
}

async function onStagingPicked(files: File | File[] | null) {
  // v-file-input with `multiple` resolves to File[] (or null when cleared).
  // Drain into the canonical imagesToUpload list, then reset the staging model
  // so the picker is ready to receive another batch.
  if (!files) {
    return;
  }
  const arr = Array.isArray(files) ? files : [files];
  if (arr.length === 0) {
    return;
  }
  await addFiles(arr);
  stagingFiles.value = [];
}
const resetAndClose = () => {
  // Abort any in-flight uploads before tearing down state. The backend
  // project (if already created) is rolled back inside uploadAllCutouts'
  // catch block once the abort propagates.
  if (uploadAbortController) {
    uploadAbortController.abort();
    uploadAbortController = null;
  }

  // Invalidate any in-flight cutout computation so it doesn't repopulate
  // `cutouts` / `croppedCutout` after we've zeroed them out below.
  cutoutGen += 1;

  emit('update:isImageOpen', false);
  createError.value = '';
  imagePreview.value = [];
  imagesToUpload.value = [];
  backgroundFiles.value = [];
  fileIds.value = [];
  resizedOnUpload.value = [];
  labelingToolValidity.value = [];
  hasMaskPixelsByIndex.value = [];
  newPart.value = cloneDeep(EMPTY_NEW_CAD_PROJECT);
  currentStepIndex.value = 1;

  // Project-level UI state — was leaking across cancel→reopen.
  advancedSettings.value = cloneDeep(DEFAULT_IMAGE_ADVANCED_SETTINGS);
  showAdvancedSettings.value = false;
  createProjectValid.value = false;
  isRetrying.value = false;

  // Clear all per-image state
  embeddingsReady.value = {};
  samMasks.value = {};
  imageEmbeddingStatus.value = {};
  imageMeta.value = {};
  labeledMasks.value = {};
  imageClasses.value = {};
  imageSizes.value = {};
  cutouts.value = {};
  croppedCutout.value = {};
  uploadFailure.value = null;

  // Bump every existing slot's epoch so any in-flight worker traffic from
  // this session is dropped on arrival, then forget the slot epochs (a new
  // session starts fresh — new files will mint new epochs via bumpSlotEpoch).
  // We post clear_all *before* clearing the local map so we can tag the
  // message with the highest epoch the worker should observe for each slot.
  if (worker.value && workerReady.value) {
    nextEpoch += 1;
    worker.value.postMessage({ type: 'clear_all', epoch: nextEpoch });
  }
  slotEpoch.value = {};
};

const addPart = async () => {
  uploadFailure.value = null;
  uploadAbortController = new AbortController();
  const { signal } = uploadAbortController;
  loadingSpinnerStore.setShowLoadingOverlay(
    true,
    false,
    $gettext('Uploading images. This might take a while'),
    'surface',
  );
  try {
    const uuid = await uploadAllCutouts(signal);
    emit('part-created', uuid);
    resetAndClose();
  } catch (err) {
    if (isAbortError(err)) {
      // User cancelled — resetAndClose has already torn the dialog down.
      return;
    }
    const detail = (err as Error)?.message ?? '';
    console.error(detail || 'Error creating part');
    uploadFailure.value = {
      message:
        $gettext(
          'Upload failed. If your images are large, resizing them to Full HD often fixes it.',
        ) + (detail ? ` (${detail})` : ''),
    };
    // Keep the parent emit for backwards-compat with any existing listeners.
    emit('file-too-large');
  } finally {
    if (uploadAbortController?.signal === signal) {
      uploadAbortController = null;
    }
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

async function retryAtFullHd() {
  isRetrying.value = true;
  uploadFailure.value = null;
  uploadAbortController = new AbortController();
  const { signal } = uploadAbortController;
  loadingSpinnerStore.setShowLoadingOverlay(
    true,
    false,
    $gettext('Resizing to Full HD and retrying upload…'),
    'surface',
  );

  // Snapshot the slices we are about to mutate so we can roll back if any
  // step fails. Without this, an error mid-retry leaves the dialog with
  // FullHD-downgraded files but inconsistent masks/cutouts and no way
  // back to the original images for the user.
  const filesSnapshot = imagesToUpload.value.slice();
  const imageMetaSnapshot: Record<number, DownsampledImage> = {
    ...imageMeta.value,
  };
  const labeledMasksSnapshot: Record<number, MaskData> = {
    ...labeledMasks.value,
  };
  const cutoutsSnapshot: Record<number, string | null> = { ...cutouts.value };
  const croppedSnapshot: Record<number, string | null> = {
    ...croppedCutout.value,
  };

  const restoreSnapshot = () => {
    imagesToUpload.value = filesSnapshot;
    imageMeta.value = imageMetaSnapshot;
    labeledMasks.value = labeledMasksSnapshot;
    cutouts.value = cutoutsSnapshot;
    croppedCutout.value = croppedSnapshot;
  };

  try {
    // Pre-flight: reject before mutating state if any mask is still
    // disconnected. This used to happen inside uploadAllCutouts after we'd
    // already swapped out the files, leaving the dialog in an unrecoverable
    // intermediate state.
    for (let i = 0; i < filesSnapshot.length; i++) {
      const mask = labeledMasks.value[i];
      if (!mask) {
        continue;
      }
      const cc = findConnectedComponents(mask.data, mask.width, mask.height);
      if (cc.numComponents > 1) {
        throw new Error(
          `Image ${i + 1} has a disconnected mask (${cc.numComponents} parts). ` +
            `Use "Keep largest" or fix the mask before retrying.`,
        );
      }
    }

    // 1. Replace any oversized source file with its FullHD version.
    //    Files already <= FullHD pass through untouched so we don't accidentally
    //    re-encode them or break the filename / mime contract.
    const resized = await Promise.all(
      filesSnapshot.map(async (f, i) => {
        const meta =
          imageMetaSnapshot[i] ?? (await downsampleImageForInference(f));
        if (meta.scale === 1) {
          return f;
        }
        return convertUrlToFile(
          meta.dataUrl,
          f.name.replace(/\.\w+$/, '.png'),
          'image/png',
        );
      }),
    );
    if (signal.aborted) {
      throw new UploadAbortedError();
    }
    imagesToUpload.value = resized;

    // 2. Refresh imageMeta so "original" dims now == FullHD; this makes
    //    getMaskAtOriginalRes a no-op so masks stay aligned. Also resample
    //    each existing labeledMask to the new dims so any drift between
    //    pre-retry mask dims and post-retry image dims is squashed up front,
    //    rather than surfacing as a silent dim mismatch in generateMaskFile
    //    or in LabelingTool's rehydrate path the next time the user opens
    //    that step.
    for (let i = 0; i < imagesToUpload.value.length; i++) {
      const newMeta = await downsampleImageForInference(
        imagesToUpload.value[i],
      );
      if (signal.aborted) {
        throw new UploadAbortedError();
      }
      imageMeta.value[i] = newMeta;
      const oldMask = labeledMasks.value[i];
      if (
        oldMask &&
        (oldMask.width !== newMeta.width || oldMask.height !== newMeta.height)
      ) {
        labeledMasks.value[i] = resampleMaskTo(
          oldMask,
          newMeta.width,
          newMeta.height,
        );
      }
    }

    // 3. Recompute cutouts against the new (FullHD) source files.
    await computeAllCutouts();
    if (signal.aborted) {
      throw new UploadAbortedError();
    }

    // 4. Retry the upload.
    loadingSpinnerStore.setShowLoadingOverlay(
      true,
      false,
      $gettext('Uploading images. This might take a while'),
      'surface',
    );
    const uuid = await uploadAllCutouts(signal);
    emit('part-created', uuid);
    resetAndClose();
  } catch (err) {
    if (isAbortError(err)) {
      restoreSnapshot();
      return;
    }
    restoreSnapshot();
    const detail = (err as Error)?.message ?? '';
    uploadFailure.value = {
      message:
        $gettext('Retry failed.') +
        (detail
          ? ` ${detail}`
          : $gettext(' Please remove some images and try again.')),
    };
  } finally {
    if (uploadAbortController?.signal === signal) {
      uploadAbortController = null;
    }
    isRetrying.value = false;
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}
const stepItems = computed(() => {
  const steps = [$gettext('Upload Images')];

  // Add a step for each uploaded image
  imagesToUpload.value.forEach((file, index) => {
    steps.push(`${$gettext('Label')} ${index + 1}: ${file.name}`);
  });

  steps.push(`${$gettext('Overview')}: ${projectName.value}`);
  return steps;
});

const isLastStep = computed(() => {
  return currentStepIndex.value === stepItems.value.length;
});

async function handleNext() {
  if (currentStepIndex.value < stepItems.value.length) {
    currentStepIndex.value++;
  } else {
    addPart();
  }
}

// Recompute the overview thumbnails whenever the user lands on the final
// step, regardless of how they got there: handleNext, handlePrev from a
// post-overview retry banner, or a direct stepper-click. handleNext used
// to do this inline, but stepper-click bypassed it and showed stale cutouts.
watch(currentStepIndex, (step) => {
  if (step === stepItems.value.length) {
    computeAllCutouts();
  }
});

function handlePrev() {
  if (currentStepIndex.value === 1) {
    resetAndClose();
  } else if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
  }
}

const canProceedToNextStep = computed(() => {
  if (currentStepIndex.value === 1) {
    return filesAreValid.value;
  }
  if (currentStepIndex.value < stepItems.value.length) {
    const index = currentStepIndex.value - 2;

    // hasMask comes from a flag the labeling tool emits — scanning the byte
    // array per-tick was an O(width*height) cost on every reactivity pass.
    // Fall back to a one-shot scan only when no flag has been emitted yet
    // (e.g. on initial back-nav before the tool has remounted).
    const hasMask =
      hasMaskPixelsByIndex.value[index] ??
      labeledMasks.value[index]?.data?.some((val) => val !== 0) ??
      false;
    const hasSize = !!(
      imageSizes.value[index]?.width && imageSizes.value[index]?.height
    );
    const hasClass = !!(
      imageClasses.value[index] && imageClasses.value[index].trim() !== ''
    );
    const inputIsValid = labelingToolValidity.value[index];

    return hasMask && hasSize && hasClass && inputIsValid;
  }
  // Overview / Finish: re-check file sizes. retryAtFullHd can replace the
  // file list mid-flow, so the gate that was true at the upload step might
  // not be true here anymore.
  return filesAreValid.value && backgroundsValid.value;
});

const nextDisabledReasons = computed<string[]>(() => {
  const reasons: string[] = [];
  if (currentStepIndex.value === 1) {
    if (imagesToUpload.value.length === 0) {
      reasons.push($gettext('Upload at least one image.'));
    } else {
      reasons.push(...fileValidationErrors.value);
    }
    return reasons;
  }
  if (currentStepIndex.value < stepItems.value.length) {
    const index = currentStepIndex.value - 2;
    const hasMask =
      hasMaskPixelsByIndex.value[index] ??
      labeledMasks.value[index]?.data?.some((val) => val !== 0) ??
      false;
    if (!hasMask) {
      reasons.push($gettext('Draw a mask around the object.'));
    }
    if (
      !(imageClasses.value[index] && imageClasses.value[index].trim() !== '')
    ) {
      reasons.push($gettext('Add a class name.'));
    }
    const size = imageSizes.value[index];
    if (!size || !size.width || !size.height || !size.depth) {
      if (isSizeLocked(index)) {
        // Locked fields can't be edited here, so we point the user to the image
        // that owns this class's dimensions.
        const cls = imageClasses.value[index];
        const owner = ownerIndexOf(cls);
        reasons.push(
          $gettext(
            'Dimensions for class "%{cls}" are set on image %{n}, please define them there.',
            { cls, n: String((owner ?? 0) + 1) },
          ),
        );
      } else {
        reasons.push($gettext('Set the X, Y and Z size in mm.'));
      }
    }
    if (
      hasMask &&
      labelingToolValidity.value[index] === false &&
      reasons.length === 0
    ) {
      reasons.push(
        $gettext(
          'The mask has multiple disconnected parts — fix it or click "Ignore" on the warning above.',
        ),
      );
    }
    return reasons;
  }
  // Overview / Finish: surface the same size errors as step 1 so the user
  // knows why the Finish button is disabled even though all labels are valid.
  if (!filesAreValid.value) {
    reasons.push(...fileValidationErrors.value);
  }
  if (!backgroundsValid.value) {
    reasons.push($gettext('Fix the background image errors below.'));
  }
  return reasons;
});

const existingClassNames = computed(() =>
  Array.from(new Set(Object.values(imageClasses.value)))
    .filter((c) => c && c.trim() !== '')
    .sort((a, b) => a.localeCompare(b)),
);

// Every label of the same class must have identical dimensions — the
// lowest-index image using a class "owns" its dimensions; all other images
// with that class get them prefilled and locked. Ownership is derived from
// the current maps (never stored) so it self-heals when images are removed
// or classes renamed.
function ownerIndexOf(cls: string | null | undefined): number | null {
  const c = cls?.trim();
  if (!c) {
    return null;
  }
  const indices = Object.entries(imageClasses.value)
    .filter(([, v]) => v === c)
    .map(([k]) => Number(k));
  return indices.length ? Math.min(...indices) : null;
}

function isSizeLocked(index: number): boolean {
  const owner = ownerIndexOf(imageClasses.value[index]);
  return owner !== null && owner !== index;
}

function onSetClass(index: number, className: string | null) {
  imageClasses.value[index] = className ?? '';
  const cls = className?.trim();
  const owner = ownerIndexOf(className);
  if (owner === null) {
    return;
  }
  if (owner !== index) {
    // Another (lower-index) image owns the class so we mirror its dims, or clear
    // ours if the owner has none set yet.
    const ownerSize = imageSizes.value[owner];
    if (ownerSize) {
      imageSizes.value[index] = { ...ownerSize };
    } else {
      delete imageSizes.value[index];
    }
    return;
  }
  // This image just became the class owner. Means we push its dims onto the other
  // members so a reclassify can't have them stay on an now old size.
  const ownerSize = imageSizes.value[index];
  if (!ownerSize) {
    return;
  }
  for (const [k, v] of Object.entries(imageClasses.value)) {
    const i = Number(k);
    if (i !== index && v === cls) {
      imageSizes.value[i] = { ...ownerSize };
    }
  }
}

function onSetSize(
  index: number,
  size: { width: number; height: number; depth: number },
) {
  // The child emits set-class then set-size; after a class switch the
  // trailing set-size still carries the old local dims and arrives before
  // the new lock prop has flowed down — drop it here, where lock state is
  // computed synchronously.
  if (isSizeLocked(index)) {
    return;
  }
  imageSizes.value[index] = size;
  const cls = imageClasses.value[index]?.trim();
  if (!cls) {
    return;
  }
  for (const [k, v] of Object.entries(imageClasses.value)) {
    const i = Number(k);
    if (i !== index && v === cls) {
      imageSizes.value[i] = { ...size };
    }
  }
}

onMounted(() => {
  initializeWorker();
});

// No blocking overlay while embeddings load — the LabelingTool surfaces a
// non-intrusive "Loading model" chip in segmentation mode, and the user can
// already work in Drawing or BBox mode without waiting.
</script>

<template>
  <!-- “Add Image Project” dialog -->
  <v-dialog
    v-model="isImageOpen"
    max-width="1400px"
    max-height="98vh"
    scrollable
    persistent
    @update:model-value="resetAndClose"
  >
    <v-card class="d-flex flex-column" style="max-height: 98vh">
      <v-card-title v-if="projectName" class="pb-2 text-truncate flex-grow-0">
        {{ projectName }}
      </v-card-title>
      <v-card-text
        style="
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        "
      >
        <!-- Upload-failure banner lives above the stepper so it stays visible
             regardless of which step the user is on. Previously this lived
             inside step 1, which meant a failure at the Overview step left
             the user staring at a blank Overview with no error feedback and
             no way to reach the retry button. -->
        <v-alert
          v-if="uploadFailure"
          type="error"
          density="compact"
          class="mb-2"
          variant="tonal"
        >
          <div class="d-flex align-center flex-wrap" style="gap: 8px">
            <span style="flex: 1 1 auto; min-width: 200px">
              {{ uploadFailure.message }}
            </span>
            <v-btn
              :loading="isRetrying"
              size="small"
              color="primary"
              variant="tonal"
              @click="retryAtFullHd"
            >
              {{ $gettext('Resize all to Full HD & retry') }}
            </v-btn>
            <v-btn size="small" variant="text" @click="uploadFailure = null">
              {{ $gettext('Dismiss') }}
            </v-btn>
          </div>
        </v-alert>
        <v-stepper
          v-model="currentStepIndex"
          :editable="createProjectValid"
          :items="stepItems"
          flat
        >
          <v-stepper-window>
            <v-stepper-window-item :value="1">
              <v-row justify="center">
                <v-col cols="12" md="10" lg="8" class="pb-12 px-6">
                  <div class="upload-controls">
                    <v-file-input
                      v-model="stagingFiles"
                      multiple
                      accept=".png,.jpg,.jpeg"
                      prepend-icon="mdi-camera"
                      density="compact"
                      hide-details
                      :clearable="false"
                      :disabled="imagesToUpload.length >= MAX_IMAGES"
                      :label="
                        imagesToUpload.length === 0
                          ? $gettext('Select images to upload')
                          : $gettext('Add more images')
                      "
                      class="upload-picker"
                      @update:model-value="onStagingPicked"
                    />
                    <span class="counter text-caption text-medium-emphasis">
                      {{
                        $gettext('%{n} of %{max} images', {
                          n: String(imagesToUpload.length),
                          max: String(MAX_IMAGES),
                        })
                      }}
                    </span>
                    <v-checkbox
                      v-model="autoResizeOnUpload"
                      density="compact"
                      hide-details
                      class="resize-toggle"
                      :disabled="imagesToUpload.length > 0"
                      :label="
                        $gettext(
                          'Resize large images to Full HD on upload (recommended for phone photos)',
                        )
                      "
                    />
                  </div>

                  <v-alert
                    v-for="(err, i) in fileValidationErrors"
                    :key="`fve-${i}`"
                    type="warning"
                    density="compact"
                    variant="tonal"
                    class="mb-2"
                  >
                    {{ err }}
                  </v-alert>

                  <div v-if="imagesToUpload.length > 0" class="image-list">
                    <div
                      v-for="(file, index) in imagesToUpload"
                      :key="fileIds[index]"
                      class="image-card"
                    >
                      <img
                        v-if="imagePreview[index]"
                        :src="imagePreview[index]"
                        class="image-thumb"
                        alt=""
                      />
                      <div v-else class="image-thumb image-thumb--placeholder">
                        <v-progress-circular
                          indeterminate
                          size="20"
                          width="2"
                        />
                      </div>
                      <div class="image-meta">
                        <div class="image-name" :title="file.name">
                          {{ file.name }}
                        </div>
                        <div
                          class="image-size text-caption text-medium-emphasis"
                        >
                          {{ formatBytes(file.size) }}
                          <span v-if="imageMeta[index]">
                            ·
                            {{ imageMeta[index].originalWidth }}×{{
                              imageMeta[index].originalHeight
                            }}
                          </span>
                          <span
                            v-if="resizedOnUpload[index]"
                            class="resized-tag"
                          >
                            · {{ $gettext('resized') }}
                          </span>
                        </div>
                      </div>
                      <v-btn
                        icon="mdi-close"
                        variant="text"
                        size="small"
                        :aria-label="$gettext('Remove image')"
                        @click="removeImageAt(index)"
                      />
                    </div>
                  </div>

                  <div class="upload-hint mt-4">
                    <p class="mb-1">
                      <strong>{{
                        $gettext('One image = one object instance.')
                      }}</strong>
                      {{
                        $gettext(
                          'In the next step you will draw a single mask per image, around the one object you want the robot to learn.',
                        )
                      }}
                    </p>
                    <p class="mb-0">
                      {{
                        $gettext(
                          'To teach the model multiple objects (different classes) or to give it more examples of the same object, upload one image for each — they will be labeled one after another.',
                        )
                      }}
                    </p>
                  </div>
                </v-col>
              </v-row>
            </v-stepper-window-item>
            <v-stepper-window-item
              v-for="(_file, index) in imagesToUpload"
              :key="fileIds[index]"
              :value="index + 2"
            >
              <div class="labeling-step-content">
                <labeling-tool
                  :image-src="imagePreview[index]"
                  :embeddings-ready="!!embeddingsReady[index]"
                  :current-mask="samMasks[index]"
                  :existing-class-names="existingClassNames"
                  :initial-mask="labeledMasks[index] ?? null"
                  :initial-class-name="imageClasses[index] ?? ''"
                  :initial-size="imageSizes[index] ?? null"
                  :size-locked="isSizeLocked(index)"
                  @create-mask="(points) => createMaskForImage(index, points)"
                  @set-class="({ className }) => onSetClass(index, className)"
                  @set-size="(size) => onSetSize(index, size)"
                  @mask-updated="(mask) => (labeledMasks[index] = mask)"
                  @validity-changed="
                    (isValid) => (labelingToolValidity[index] = isValid)
                  "
                  @has-pixels-changed="
                    (hasPixels) => (hasMaskPixelsByIndex[index] = hasPixels)
                  "
                />
              </div>
            </v-stepper-window-item>
            <v-stepper-window-item :value="stepItems.length">
              <div class="scrollable-container">
                <div class="d-flex justify-space-between align-center mb-4">
                  <h3>{{ $gettext('Overview') }}: {{ projectName }}</h3>
                  <!-- Advanced Settings Toggle -->
                  <v-btn
                    variant="text"
                    size="small"
                    class="text-caption"
                    @click="showAdvancedSettings = !showAdvancedSettings"
                  >
                    <v-icon start>
                      {{ showAdvancedSettings ? 'mdi-chevron-up' : 'mdi-cog' }}
                    </v-icon>
                    {{ $gettext('Advanced') }}
                  </v-btn>
                </div>
                <ImageProjectOverview
                  :thumbnails="croppedCutout"
                  :image-classes="imageClasses"
                  :image-sizes="imageSizes"
                />
                <CustomBackgroundsInput
                  v-model="backgroundFiles"
                  v-model:valid="backgroundsValid"
                  class="mt-4"
                />
                <AdvancedSettingsInput
                  v-if="showAdvancedSettings"
                  :showCADOptions="false"
                  :advancedSettings="advancedSettings"
                  :isGlbFile="false"
                ></AdvancedSettingsInput>
              </div>
            </v-stepper-window-item>
          </v-stepper-window>
          <template v-slot:prev>
            <div class="d-flex ga-2">
              <v-btn
                rounded="pill"
                variant="text"
                color="error"
                :disabled="false"
                @click="resetAndClose"
              >
                {{ $gettext('Cancel') }}
              </v-btn>
              <v-btn
                v-if="currentStepIndex > 1"
                rounded="pill"
                variant="text"
                style="margin-left: -20px"
                :disabled="false"
                @click="handlePrev"
              >
                {{ $gettext('Back') }}
              </v-btn>
            </div>
          </template>
          <template v-slot:next>
            <v-tooltip
              location="top"
              :disabled="canProceedToNextStep"
              :open-on-hover="!canProceedToNextStep"
            >
              <template #activator="{ props: a }">
                <!-- Wrapping span keeps the tooltip alive while the inner btn is disabled. -->
                <span v-bind="a" style="margin-right: -20px">
                  <v-btn
                    color="primary"
                    variant="text"
                    rounded="pill"
                    :disabled="!canProceedToNextStep"
                    @click="handleNext"
                  >
                    {{ isLastStep ? $gettext('Finish') : $gettext('Next') }}
                  </v-btn>
                </span>
              </template>
              <div>
                <strong>{{ $gettext('Before continuing:') }}</strong>
                <ul style="padding-left: 18px; margin: 4px 0 0">
                  <li v-for="(reason, i) in nextDisabledReasons" :key="i">
                    {{ reason }}
                  </li>
                </ul>
              </div>
            </v-tooltip>
          </template>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.v-stepper {
  .v-stepper-window {
    flex: 1; /* fill remaining space below the header */
    min-height: 0; /* allow its children to shrink below their content height */
    display: flex;
    padding-top: 0;
    margin: 0;
    flex-direction: column;
    overflow: hidden;
  }
}

.stepper-fixed-height .v-stepper-window {
  flex: 1; /* fill remaining space below the header */
  min-height: 0; /* allow its children to shrink below their content height */
  display: flex;
  padding-top: 0;
  margin-top: 0;
  flex-direction: column;
  overflow: hidden;
}

.stepper-window-scroll {
  flex: 1;
  overflow-y: auto;
}

.stepper-buttons {
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 16px;
}

.scrollable-container {
  flex: 1;
  min-height: 0;
  max-height: 65vh;
  overflow-y: auto;
  padding: 16px;
}

.v-dialog .labeling-step-content {
  min-height: 65vh;
  display: flex;
  flex-direction: column;
}

.flex-grow-1 {
  flex-grow: 1;
}

.upload-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 4px;
}

.upload-controls .upload-picker {
  flex: 1 1 320px;
  min-width: 280px;
}

.upload-controls .counter {
  font-size: 12px;
  flex: 0 0 auto;
}

.upload-controls .resize-toggle {
  flex: 0 1 auto;
}

.upload-controls .resize-toggle :deep(.v-selection-control) {
  min-height: 32px;
}

.upload-hint {
  font-size: 13px;
  line-height: 1.4;
  color: #475569;
}

.image-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 8px;
  max-height: 50vh;
  overflow-y: auto;
}

.image-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.image-thumb {
  width: 56px;
  height: 56px;
  flex: 0 0 auto;
  object-fit: cover;
  border-radius: 6px;
  background: #f1f5f9;
}

.image-thumb--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-meta {
  flex: 1 1 auto;
  min-width: 0;
}

.image-name {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-size {
  font-size: 11px;
}
</style>
